import React, { useState, useEffect } from "react";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import {
  faHeart as faHeartSolid,
  faShoppingCart,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PublicProductList } from "interfaces/IProduct";
import { useAuthStore } from "../../../../zustand/loginStore";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useBreakpoints } from "hooks/useBreakPoints";
import { useNavigate } from "react-router-dom";
import { createAuthenticatedAxiosInstance, toggleFavorite } from "api/auth";
import { useUserStore } from "../../../../zustand/userStore";
import { IconButton } from "@mui/material";

interface ProductCardProps {
  product: PublicProductList;
  className?: string;
  handleProductClick: (product: PublicProductList) => void;
  handleToast: (message: string, show: boolean, success: boolean) => void;
}

const SearchProductCard: React.FC<ProductCardProps> = ({
  product,
  className,
  handleProductClick,
  handleToast,
}) => {
  const { favoriteProducts, setFavoriteProducts } = useUserStore();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageOpacity, setImageOpacity] = useState(1);
  const { token } = useAuthStore();
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { isMobile } = useBreakpoints();
  const [tooltipOpacity, setTooltipOpacity] = useState(1);
  const navigate = useNavigate();
  const [heartDisabled, setHeartDisabled] = useState<boolean>(false);
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [isFavorite, setIsFavorite] = useState(false);

  const handleHeartClick = () => {
    setShowTooltip(true);
    setTooltipOpacity(1);

    setTimeout(() => {
      setTooltipOpacity(0);
      setTimeout(() => {
        setShowTooltip(false);
      }, 1000);
    }, 1500);
  };

  const handleFavoriteClick = async () => {
    setHeartDisabled(true);
    if (token) {
      const { data, success } = await toggleFavorite(axiosInstance, product.id);
      if (!success) {
        handleToast(
          `El producto no pudo ser ${
            isFavorite ? "removido de" : "agregado a"
          } a la lista de favoritos, por favor intente nuevamente.`,
          true,
          false
        );
        setHeartDisabled(false);
        return;
      }
      data && setFavoriteProducts(data);
      handleToast(
        `El producto fue ${
          isFavorite ? "removido de" : "agregado a"
        } la lista de favoritos.`,
        true,
        true
      );
    }
    setHeartDisabled(false);
  };

  const favoriteTooltip = (
    <Tooltip
      id="favorite-tooltip"
      className="custom-tooltip-client"
      style={{
        opacity: tooltipOpacity,
        lineHeight: "1.3",
        transition: "opacity 0.5s ease-in-out",
      }}
    >
      Inicia sesión o crea
      <br />
      una cuenta para
      <br />
      guardar tus artículos
      <br />
      favoritos!
    </Tooltip>
  );

  const heartIcon = (
    <IconButton
      disabled={heartDisabled}
      onClick={(e) => {
        e.stopPropagation();
        handleFavoriteClick();
      }}
      style={{ color: "#997404" }}
    >
      <FontAwesomeIcon
        icon={isFavorite ? faHeartSolid : faHeartRegular}
        className="icon"
        style={{ cursor: "pointer" }}
      />
    </IconButton>
  );

  useEffect(() => {
    if (
      !product.attributes[0]?.images ||
      product.attributes[0]?.images.length <= 1 ||
      !isHovered
    )
      return;

    const timer = setInterval(() => {
      setImageOpacity(0);
      setTimeout(() => {
        setCurrentImageIndex(
          (prev) => (prev + 1) % product.attributes[0].images.length
        );
        setImageOpacity(1);
      }, 500);
    }, 1500);

    return () => clearInterval(timer);
  }, [product.attributes, isHovered]);

  /**
   * effect for favorites to find them in the store
   */
  useEffect(() => {
    setIsFavorite(false);
    favoriteProducts.forEach((fav) => {
      if (product.id === fav.product_id) {
        setIsFavorite(true);
        return;
      }
    });
  }, [favoriteProducts]);
  return (
    <div
      className={`search-product-card ${className || ""}`}
      onClick={() => navigate(`/product-details/${product.id}`)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        border: isHovered ? "3px solid #997404" : "3px solid transparent",
        transition: "border-color 0.3s ease-in-out",
      }}
    >
      <div className="image-container">
        <img
          src={product.attributes[0]?.images[currentImageIndex] || ""}
          alt={product.name}
          style={{ "--opacity": imageOpacity } as React.CSSProperties}
        />
      </div>
      <div className="product-name-container">
        <h3 style={{ fontSize: isMobile ? "15px" : "20px" }}>{product.name}</h3>
      </div>
      {!isMobile && (
        <>
          <div className="separator-line" />
          <div className="sizes-container">
            <span className="size-tag">Tallas</span>
            {product.attributes[0]?.sizes.map((size) => (
              <span key={size.size} className="size-tag">
                {size.size}
              </span>
            ))}
          </div>
          <div className="separator-line" />
        </>
      )}
      <div className="bottom-section">
        <span className="price">${product.attributes[0]?.sizes[0]?.price}</span>
        <div className="icons">
          {token ? (
            heartIcon
          ) : (
            <OverlayTrigger
              show={showTooltip}
              trigger="click"
              placement="top"
              overlay={favoriteTooltip}
              rootClose
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleHeartClick();
                }}
                style={{ cursor: "pointer", color: "#997404" }}
              >
                {heartIcon}
              </IconButton>
            </OverlayTrigger>
          )}
          {!isMobile && (
            <IconButton
              style={{ color: "#997404" }}
              onClick={(e) => {
                e.stopPropagation();
                handleProductClick(product);
              }}
            >
              <FontAwesomeIcon
                icon={faShoppingCart}
                className="icon"
                style={{ cursor: "pointer", color: "#997404" }}
              />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchProductCard;
